import { z } from 'zod';
import { applicationKeySchema, accessContextTypeSchema } from './products.js';
import { normalizeOrigin } from './navigation.js';
/**
 * Application registry. One entry per application per environment. Public metadata (served by
 * GET /v1/applications and safe to bundle into frontends) is kept apart from backend configuration.
 * Hostnames live here and in environment configuration only, never inside components.
 */
export const APPLICATION_STATUSES = ['available', 'preview', 'unavailable'];
export const ENVIRONMENTS = ['development', 'test', 'staging', 'production'];
export const environmentSchema = z.enum(ENVIRONMENTS);
const originSchema = z.string().transform((value, ctx) => {
    try {
        return normalizeOrigin(value);
    }
    catch (error) {
        ctx.addIssue({ code: 'custom', message: `invalid origin ${value}: ${error.message}` });
        return z.NEVER;
    }
});
const defaultPathSchema = z
    .string()
    .regex(/^\/(?!\/)/, 'default path must be a same-origin absolute path')
    .max(512);
export const registeredApplicationSchema = z.object({
    key: applicationKeySchema,
    displayName: z.string(),
    context: accessContextTypeSchema.optional(),
    /** Account-hub route that renders this application's sign-in page. */
    entryRoute: z.string(),
    status: z.enum(APPLICATION_STATUSES),
    /**
     * Where a signed-in person is sent. Null means not deployed in this environment: the hub shows an
     * unavailable state instead of guessing a URL.
     */
    destination: z
        .object({
        origin: originSchema,
        defaultPath: defaultPathSchema,
        /** Labels a local navigation fixture so it can never be mistaken for the product. */
        fixture: z.boolean().default(false),
    })
        .nullable(),
    /** API audience the application requests tokens for. */
    audience: z.string().optional(),
    /** Public identity-provider client id of this application's own registration (not a secret). */
    auth0ClientId: z.string().optional(),
});
export const applicationRegistrySchema = z
    .object({
    environment: environmentSchema,
    hub: z.object({ origin: originSchema }),
    applications: z.array(registeredApplicationSchema),
})
    .superRefine((registry, ctx) => {
    const seen = new Set();
    for (const app of registry.applications) {
        if (seen.has(app.key))
            ctx.addIssue({ code: 'custom', message: `duplicate application ${app.key}` });
        seen.add(app.key);
        if (registry.environment === 'production' && app.destination?.fixture) {
            ctx.addIssue({ code: 'custom', message: `${app.key}: navigation fixtures are not allowed in production` });
        }
        if (registry.environment === 'production' && app.destination?.origin.startsWith('http:')) {
            ctx.addIssue({ code: 'custom', message: `${app.key}: production destinations must use https` });
        }
    }
});
export function findApplication(registry, key) {
    return registry.applications.find((a) => a.key === key);
}
//# sourceMappingURL=applications.js.map